import { Badge } from '@ophelos/ui/components/badge';
import { Separator } from '@ophelos/ui/components/separator';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@ophelos/ui/components/sidebar';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import type { NavItemVisibility } from '@/lib/app-navigation';
import { useHubPathname } from '@/lib/global/hub-path';
import { HubNavLink } from './HubNavLink';

export interface NavItem {
  name: string;
  to: string;
  icon: LucideIcon;
  disabled?: boolean;
  visibility?: NavItemVisibility;
  /** Count of things waiting on the user; shown as a red bubble when above zero. */
  badge?: number;
  /** Control pinned to the item's right edge, outside the link so it never navigates (expanded sidebar only). */
  trailing?: ReactNode;
}

/** The item's red count bubble: beside the label, or on the icon when the sidebar is collapsed. */
export function NavItemCountBadge({ count, collapsed }: { count?: number; collapsed: boolean }) {
  if (!count) return null;
  const label = count > 99 ? '99+' : count;
  return collapsed ? (
    <Badge variant="failure" size="number" className="pointer-events-none absolute -top-1 -right-1">
      {label}
    </Badge>
  ) : (
    <Badge variant="failure" className="ml-auto">
      {label}
    </Badge>
  );
}

/** Reusable sidebar nav group with optional label and item filtering */
export function SidebarNavGroup({ items, label, className }: { items: NavItem[]; label?: string; className?: string }) {
  const pathname = useHubPathname();
  const collapsed = useSidebar().state === 'collapsed';
  const visibleItems = items.filter((item) => !item.disabled);

  if (visibleItems.length === 0) return null;

  return (
    <SidebarGroup className={className}>
      {label && (
        <SidebarGroupLabel className="text-muted-foreground uppercase text-xs font-medium tracking-widest">
          {label}
          <Separator orientation="horizontal" className="flex-1 ml-2 bg-border" />
        </SidebarGroupLabel>
      )}
      <SidebarGroupContent>
        <SidebarMenu>
          {visibleItems.map((item) => {
            const isActive = pathname === item.to || pathname.startsWith(`${item.to}/`);
            return (
              <SidebarMenuItem key={item.to}>
                <SidebarMenuButton asChild isActive={isActive} tooltip={item.name}>
                  <HubNavLink to={item.to}>
                    <item.icon />
                    <span>{item.name}</span>
                    {!collapsed && <NavItemCountBadge count={item.badge} collapsed={false} />}
                  </HubNavLink>
                </SidebarMenuButton>
                {collapsed && <NavItemCountBadge count={item.badge} collapsed />}
                {!collapsed && item.trailing && (
                  // data-sidebar="menu-action" makes the sibling menu button reserve right padding for it.
                  <div data-sidebar="menu-action" className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center">
                    {item.trailing}
                  </div>
                )}
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
